import { GetStaticProps, NextPage } from 'next'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { Footer } from 'src/components/Footer/Footer'
import Editor from 'src/components/global/Editor'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { fundingAmountsC, fundingInfosC } from 'src/constants/constants'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { IEnum_Solution_Type } from 'src/graphql/generated/hooks'
import { useAdminEmailCheck } from 'src/hooks/useAdminEmailCheck'
import { useAuthDefender } from 'src/hooks/useAuthDefender'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { getFullSolutionsBySlugs } from 'src/utils/getSolutionsBySlugs'
import { SolutionDataFragment } from 'src/utils/SolutionDataFragment'

interface ComparisonProps {
  solution: SolutionDataFragment | null
  solutionReqs: SolutionDataFragment | null
  slug: string
}

const Comparison: NextPage<ComparisonProps> = ({ solution, solutionReqs, slug }) => {
  useAuthDefender()
  const router = useRouter()
  const { isAdmin, isCheckingAdmin } = useAdminEmailCheck()
  useEffect(() => {
    if (!isCheckingAdmin && !isAdmin) {
      router.push('/restricted-access')
    }
  }, [isAdmin, isCheckingAdmin])
  const keysToRemove = ['id', 'createdAt', 'publishedAt']
  const [difference, setdifference] = useState({})
  const solutionType = solution?.attributes?.type

  // Show a fallback loading state if Next.js is statically generating the page for the first time
  if (router.isFallback) {
    return <div>Loading...</div>
  }

  // Function to remove keys from the solution
  const removeKeys = (obj: any, keysToRemove: string[]): any => {
    if (Array.isArray(obj)) {
      return obj.map((item) => removeKeys(item, keysToRemove))
    } else if (typeof obj === 'object' && obj !== null) {
      const newObj: any = {}
      for (const key in obj) {
        // Skip keys that are included in the keysToRemove array
        if (!keysToRemove.includes(key) && Object.hasOwnProperty.call(obj, key)) {
          newObj[key] = removeKeys(obj[key], keysToRemove)
        }
      }
      return newObj
    }
    return obj
  }

  // Function to remove role and bio from founders
  const removeRoleAndBioFromFounders = (solution: any) => {
    if (solution?.attributes?.founders) {
      solution.attributes.founders = solution.attributes.founders.map((founder: any) => {
        const { role, bio, ...rest } = founder
        // Replace null values with an empty string
        const cleanedFounder = Object.keys(rest).reduce((acc: any, key: string) => {
          acc[key] = rest[key] === null ? '' : rest[key]
          return acc
        }, {})
        return cleanedFounder
      })
    }
    return solution
  }

  // Function to compare objects deeply
  const deepEqual = (obj1: any, obj2: any) => {
    if (obj1 === obj2) return true
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) return false

    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)

    if (keys1.length !== keys2.length) return false

    for (const key of keys1) {
      if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false
    }

    return true
  }

  // Removing role and bio from founders
  solution = removeRoleAndBioFromFounders(solution)
  solution = removeKeys(solution, keysToRemove)
  solutionReqs = removeKeys(solutionReqs, keysToRemove)

  const descOtherIntegration1 = (solution?.attributes as { descOtherIntegration?: string })?.descOtherIntegration
  const descOtherIntegration2 = (solutionReqs?.attributes as { descOtherIntegration?: string })?.descOtherIntegration

  // Finding the difference in the solutions
  const findDifferences = (obj1: any, obj2: any): any => {
    const differences: any = {}

    const isObject = (obj: any) => obj && typeof obj === 'object' && !Array.isArray(obj)

    const processSpecialKeys = (key: string, obj: any, descField: string) => {
      if (obj[key]?.data?.length === 1 && obj[key].data[0]?.attributes?.name === 'Others') {
        return {
          data: [
            {
              attributes: { name: descField },
            },
          ],
        }
      }
      return obj[key]
    }
    for (const key in obj1) {
      if (Object.hasOwnProperty.call(obj1, key) && Object.hasOwnProperty.call(obj2, key) && key !== 'id') {
        let val1 = obj1[key]
        let val2 = obj2[key]
        if (key === 'existingCustomers') {
          val1 = processSpecialKeys(key, obj1, obj1.descOtherExistingCustomer || null)
          val2 = processSpecialKeys(key, obj2, obj2.descOtherExistingCustomer || null)
        } else if (key === 'integrations') {
          val1 = processSpecialKeys(key, obj1, descOtherIntegration1 ?? '')
          val2 = processSpecialKeys(key, obj2, descOtherIntegration2 ?? '')
        }
        if ((val1 === null && val2 === '') || (val1 === '' && val2 === null)) {
          continue
        }
        if (key === 'vendorResources' && Array.isArray(val1) && Array.isArray(val2)) {
          const combinedFrom: any[] = []
          const combinedTo: any[] = []

          for (let i = 0; i < Math.max(val1.length, val2.length); i++) {
            const resource1 = val1[i]?.external || {}
            const resource2 = val2[i]?.external || {}

            const t1 = resource1.title || null
            const t2 = resource2.title || null
            const l1 = resource1.link || null
            const l2 = resource2.link || null

            if (!(t1 === null && l1 === null)) {
              combinedFrom.push({
                title: t1,
                link: l1,
              })
            }
            if (!(t2 === null && l2 === null)) {
              combinedTo.push({
                title: t2,
                link: l2,
              })
            }
          }

          if (!deepEqual(combinedFrom, combinedTo)) {
            differences[key] = { from: combinedFrom, to: combinedTo }
          }
        } else if (isObject(val1) && isObject(val2)) {
          const nestedDifferences = findDifferences(val1, val2)
          if (Object.keys(nestedDifferences).length > 0) {
            differences[key] = nestedDifferences
          }
        } else if (!deepEqual(val1, val2)) {
          differences[key] = { from: val1, to: val2 }
        }
      }
    }
    return differences
  }

  useEffect(() => {
    if (solution && solutionReqs) {
      const dif = findDifferences(solution.attributes, solutionReqs.attributes)
      if (!deepEqual(dif, difference)) {
        setdifference(dif)
      }
    }
  }, [solution, solutionReqs, difference])

  // console.log(111, solution)
  // console.log(222, solutionReqs)
  // console.log(55, difference)

  const title = 'Difference in Solution'
  return (
    <Main meta={<Meta title={title} description="" url={`/admin/solution_request/${slug}`} />}>
      <MainContainer id="mainContainer">
        <div className="flex flex-col bg-clouds">
          <div className="flex flex-row px-5 sm:px-0 justify-center">
            <div className="!max-w-[1100px] w-full flex flex-row items-center py-2 mt-[0.9375rem]">
              <div className="flex flex-row gap-4" onClick={() => router.push('/')}>
                <span className="text-textdark font-primary font-bold text-[13px] tracking-normal cursor-pointer">
                  BACK TO HOME PAGE
                </span>
              </div>
            </div>
          </div>
        </div>
        <hr />
        <h1 className="text-2xl font-bold text-center my-8">The Edits</h1>
        <div className="flex flex-col items-center">
          {!solution ? (
            <div className="flex flex-row justify-center w-full">
              <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-[20px] min-h-[300px]">
                Solution does not exist
              </div>
            </div>
          ) : !solutionReqs ? (
            <div className="flex flex-row justify-center w-full">
              <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-[20px] min-h-[300px]">
                Solution does not exist in solution-request
              </div>
            </div>
          ) : Object.keys(difference).length === 0 ? (
            <div className="flex flex-row justify-center w-full">
              <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-[20px] min-h-[300px]">
                Solutions is identical to solution present in solution-request
              </div>
            </div>
          ) : (
            <>
              <div className="max-w-[1100px] mx-auto mb-20">
                <div className="max-w-[1100px] mx-auto mb-4 text-lg font-bold text-left">
                  {solution?.attributes?.displayName}
                </div>
                <table className="w-full table-fixed border-collapse">
                  <thead>
                    <tr>
                      <th className="bg-gray-200 p-4 w-[150px] text-left">Fields</th>
                      <th className="bg-gray-200 p-4 w-[300px] text-center border-l">Original Solution</th>
                      <th className="bg-gray-200 p-4 w-[300px] text-center border-l">Solution Request</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(difference as any)?.subTopics && (
                      <tr>
                        <td className="p-4 border-b">Category</td>
                        <td className="p-4 border-b border-l break-words">
                          {(difference as any)?.subTopics?.data?.from.map((item: any) => (
                            <div key={item?.id}>{item?.attributes?.name}</div>
                          ))}
                        </td>
                        <td className="p-4 border-b border-l break-words">
                          {(difference as any)?.subTopics?.data?.to.map((item: any) => (
                            <div key={item?.id}>{item?.attributes?.name}</div>
                          ))}
                        </td>
                      </tr>
                    )}
                    {(difference as any)?.logo && (
                      <tr>
                        <td className="p-4 border-b">Logo</td>
                        <td className="p-4 border-b border-l break-all">
                          <Image
                            src={(difference as any)?.logo?.data?.attributes?.url?.from}
                            alt="Logo From"
                            width={100}
                            height={100}
                            className="object-contain"
                          />
                        </td>
                        <td className="p-4 border-b border-l break-all">
                          <Image
                            src={(difference as any)?.logo?.data?.attributes?.url?.to}
                            alt="Logo To"
                            width={100}
                            height={100}
                            className="object-contain"
                          />
                        </td>
                      </tr>
                    )}
                    {(difference as any)?.website && (
                      <tr>
                        <td className="p-4 border-b">Website</td>
                        <td className="p-4 border-b border-l break-all">
                          <a href={(difference as any)?.website?.from} className="text-blue-600 underline break-all">
                            {(difference as any)?.website?.from}
                          </a>
                        </td>
                        <td className="p-4 border-b border-l break-all">
                          <a href={(difference as any)?.website?.to} className="text-blue-600 underline break-all">
                            {(difference as any)?.website?.to}
                          </a>
                        </td>
                      </tr>
                    )}
                    {(difference as any)?.linkedin && (
                      <tr>
                        <td className="p-4 border-b">LinkedIn</td>
                        <td className="p-4 border-b border-l break-words">
                          <a href={(difference as any)?.linkedin?.from} className="text-blue-600 underline break-all">
                            {(difference as any)?.linkedin?.from}
                          </a>
                        </td>
                        <td className="p-4 border-b border-l break-words">
                          <a href={(difference as any)?.linkedin?.to} className="text-blue-600 underline break-all">
                            {(difference as any)?.linkedin?.to}
                          </a>
                        </td>
                      </tr>
                    )}
                    {(difference as any)?.twitter && (
                      <tr>
                        <td className="p-4 border-b">twitter</td>
                        <td className="p-4 border-b border-l break-words">
                          <a href={(difference as any)?.twitter?.from} className="text-blue-600 underline break-all">
                            {(difference as any)?.twitter?.from}
                          </a>
                        </td>
                        <td className="p-4 border-b border-l break-words">
                          <a href={(difference as any)?.twitter?.to} className="text-blue-600 underline break-all">
                            {(difference as any)?.twitter?.to}
                          </a>
                        </td>
                      </tr>
                    )}
                    {(difference as any)?.shortDescription && (
                      <tr>
                        <td className="p-4 border-b">Short Description</td>
                        <td className="p-4 border-b border-l break-words">
                          <Editor
                            initialValue={(difference as any)?.shortDescription?.from}
                            onEditorChange={() => {}}
                            readOnly={true}
                          />
                        </td>
                        <td className="p-4 border-b border-l break-words">
                          <Editor
                            initialValue={(difference as any)?.shortDescription?.to}
                            onEditorChange={() => {}}
                            readOnly={true}
                          />
                        </td>
                      </tr>
                    )}
                    {(difference as any)?.longDescription && (
                      <tr>
                        <td className="p-4 border-b">Long Description</td>
                        <td className="p-4 border-b border-l break-words">
                          <Editor
                            initialValue={(difference as any)?.longDescription?.from}
                            onEditorChange={() => {}}
                            readOnly={true}
                          />
                        </td>
                        <td className="p-4 border-b border-l break-words">
                          <Editor
                            initialValue={(difference as any)?.longDescription?.to}
                            onEditorChange={() => {}}
                            readOnly={true}
                          />
                        </td>
                      </tr>
                    )}
                    {(difference as any)?.offices && (
                      <>
                        <tr>
                          <td className="p-4 border-b">Offices</td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.offices?.data?.from.map((item: any) => (
                              <div key={item?.id}>{item?.attributes?.name}</div>
                            ))}
                          </td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.offices?.data?.to.map((item: any) => (
                              <div key={item?.id}>{item?.attributes?.name}</div>
                            ))}
                          </td>
                        </tr>
                      </>
                    )}
                    {(difference as any)?.hqs && (
                      <>
                        <tr>
                          <td className="p-4 border-b">Headquarters</td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.hqs?.data?.from.map((item: any) => (
                              <div key={item?.id}>{item?.attributes?.name}</div>
                            ))}
                          </td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.hqs?.data?.to.map((item: any) => (
                              <div key={item?.id}>{item?.attributes?.name}</div>
                            ))}
                          </td>
                        </tr>
                      </>
                    )}

                    {(difference as any)?.regionsServed && (
                      <>
                        <tr>
                          <td className="p-4 border-b">Regions Served</td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.regionsServed?.data?.from.map((item: any) => (
                              <div key={item?.id}>{item?.attributes?.name}</div>
                            ))}
                          </td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.regionsServed?.data?.to.map((item: any) => (
                              <div key={item?.id}>{item?.attributes?.name}</div>
                            ))}
                          </td>
                        </tr>
                      </>
                    )}

                    {(difference as any)?.languages && (
                      <>
                        <tr>
                          <td className="p-4 border-b">Languages</td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.languages?.data?.from.map((item: any) => (
                              <div key={item?.id}>{item?.attributes?.name}</div>
                            ))}
                          </td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.languages?.data?.to.map((item: any) => (
                              <div key={item?.id}>{item?.attributes?.name}</div>
                            ))}
                          </td>
                        </tr>
                      </>
                    )}

                    {(difference as any)?.vendorResources && (
                      <>
                        <tr>
                          <td className="p-4 border-b">Vendor Resources</td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.vendorResources?.from.map((item: any, index: number) => (
                              <div className="py-2 border-b" key={index}>
                                <span>Title: {item?.title}</span>
                                <br />
                                <span>
                                  Link:{' '}
                                  <a
                                    href={item?.link}
                                    className="text-blue-600 underline break-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {item?.link}
                                  </a>
                                </span>
                              </div>
                            ))}
                          </td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.vendorResources?.to.map((item: any, index: number) => (
                              <div className="py-2 border-b" key={index}>
                                <span>Title: {item?.title}</span>
                                <br />
                                <span>
                                  Link:{' '}
                                  <a
                                    href={item?.link}
                                    className="text-blue-600 underline break-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {item?.link}
                                  </a>
                                </span>
                              </div>
                            ))}
                          </td>
                        </tr>
                      </>
                    )}

                    {(difference as any)?.existingCustomers && (
                      <>
                        <tr>
                          <td className="p-4 border-b">Existing Customers</td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.existingCustomers?.data?.from.map((item: any) => (
                              <div key={item?.id}>{item?.attributes?.name}</div>
                            ))}
                          </td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.existingCustomers?.data?.to.map((item: any) => (
                              <div key={item?.id}>{item?.attributes?.name}</div>
                            ))}
                          </td>
                        </tr>
                      </>
                    )}
                    {(difference as any)?.yearFounded && (
                      <>
                        <tr>
                          <td className="p-4 border-b">Year Founded</td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.yearFounded?.from}
                          </td>
                          <td className="p-4 border-b border-l break-words">{(difference as any)?.yearFounded?.to}</td>
                        </tr>
                      </>
                    )}
                    {(difference as any)?.founders && (
                      <>
                        <tr>
                          <td className="p-4 border-b">Founders</td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.founders?.from.map((item: any, index: number) => (
                              <div className="py-2 border-b" key={index}>
                                <span>Name: {item?.name}</span>
                                <br />
                                <span>
                                  Link:
                                  <a
                                    href={item?.externalLink}
                                    className="text-blue-600 underline break-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {item?.externalLink}
                                  </a>
                                </span>
                                <br />
                                <span>Title: {item?.title}</span>
                                <br />
                                <span>Avatar: </span>
                                <br />
                                {item?.avatar?.data?.attributes?.url && (
                                  <Image
                                    src={item?.avatar?.data?.attributes?.url}
                                    alt={`${item?.name}'s Avatar`}
                                    width={50}
                                    height={50}
                                    className="object-contain rounded-full"
                                  />
                                )}
                              </div>
                            ))}
                          </td>
                          <td className="p-4 border-b border-l break-words">
                            {(difference as any)?.founders?.to.map((item: any, index: number) => (
                              <div className="py-2 border-b" key={index}>
                                <span>Name: {item?.name}</span>
                                <br />
                                <span>
                                  Link:
                                  <a
                                    href={item?.externalLink}
                                    className="text-blue-600 underline break-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {item?.externalLink}
                                  </a>
                                </span>
                                <br />
                                <span>Title: {item?.title}</span>
                                <br />
                                <span>Avatar: </span>
                                <br />
                                {item?.avatar?.data?.attributes?.url && (
                                  <Image
                                    src={item?.avatar?.data?.attributes?.url}
                                    alt={`${item?.name}'s Avatar`}
                                    width={50}
                                    height={50}
                                    className="object-contain rounded-full"
                                  />
                                )}
                              </div>
                            ))}
                          </td>
                        </tr>
                      </>
                    )}
                    {solutionType === 'Product' && (
                      <>
                        {(difference as any)?.productData?.tool && (
                          <tr>
                            <td className="p-4 border-b">Product Name</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.tool?.from}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.tool?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.audiences && (
                          <tr>
                            <td className="p-4 border-b">Audiences</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.audiences?.data?.from.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.audiences?.data?.to.map((item: any, index: number) => (
                                <div key={index}>{item?.attributes?.name}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.attributes && (
                          <tr>
                            <td className="p-4 border-b">Attributes</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.attributes?.data?.from.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.attributes?.data?.to.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.deployments && (
                          <tr>
                            <td className="p-4 border-b">Deployments</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.deployments?.data?.from.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.deployments?.data?.to.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.integrations && (
                          <tr>
                            <td className="p-4 border-b">Integrations</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.integrations?.data?.from.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.integrations?.data?.to.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.practiceAreas && (
                          <tr>
                            <td className="p-4 border-b">Practice Areas</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.practiceAreas?.data?.from.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.practiceAreas?.data?.to.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.typeOfUser && (
                          <tr>
                            <td className="p-4 border-b">Type Of User</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.typeOfUser?.data?.from.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.typeOfUser?.data?.to.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.fundingAmount && (
                          <tr>
                            <td className="p-4 border-b">Funding Amount</td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingAmountsC.find(
                                  (item: any) => item.value === (difference as any)?.productData?.fundingAmount?.from,
                                )?.label
                              }
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingAmountsC.find(
                                  (item: any) => item.value === (difference as any)?.productData?.fundingAmount?.to,
                                )?.label
                              }
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.fundingInfo && (
                          <tr>
                            <td className="p-4 border-b">Funding Info</td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingInfosC.find(
                                  (item: any) => item.value === (difference as any)?.productData?.fundingInfo?.from,
                                )?.label
                              }
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingInfosC.find(
                                  (item: any) => item.value === (difference as any)?.productData?.fundingInfo?.to,
                                )?.label
                              }
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.valueProposition && (
                          <tr>
                            <td className="p-4 border-b">Value Proposition</td>
                            <td className="p-4 border-b border-l break-words">
                              <Editor
                                initialValue={(difference as any)?.productData?.valueProposition?.from}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              <Editor
                                initialValue={(difference as any)?.productData?.valueProposition?.to}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.security && (
                          <tr>
                            <td className="p-4 border-b">Security</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.security?.data?.from.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.productData?.security?.data?.to.map((item: any, index: number) => (
                                <div key={index}>{item?.attributes?.name}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.enhancedListingData?.callToAction && (
                          <tr>
                            <td className="p-4 border-b">Call to Action</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.productData?.enhancedListingData?.callToAction?.label?.from && (
                                <>
                                  Label:{' '}
                                  {(difference as any)?.productData?.enhancedListingData?.callToAction?.label?.from}
                                  <br />
                                </>
                              )}
                              {(difference as any)?.productData?.enhancedListingData?.callToAction?.url?.from && (
                                <>
                                  URL:
                                  <a
                                    href={
                                      (difference as any)?.productData?.enhancedListingData?.callToAction?.url?.from
                                    }
                                    className="text-blue-600 underline break-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {(difference as any)?.productData?.enhancedListingData?.callToAction?.url?.from}
                                  </a>
                                </>
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.productData?.enhancedListingData?.callToAction?.label?.to && (
                                <>
                                  Label:{' '}
                                  {(difference as any)?.productData?.enhancedListingData?.callToAction?.label?.to}
                                  <br />
                                </>
                              )}
                              {(difference as any)?.productData?.enhancedListingData?.callToAction?.url?.to && (
                                <>
                                  URL:
                                  <a
                                    href={(difference as any)?.productData?.enhancedListingData?.callToAction?.url?.to}
                                    className="text-blue-600 underline break-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {(difference as any)?.productData?.enhancedListingData?.callToAction?.url?.to}
                                  </a>
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.enhancedListingData?.pricingData && (
                          <tr>
                            <td className="p-4 border-b">Pricing Data</td>
                            <td className="p-4 border-b border-l break-all">
                              <Editor
                                initialValue={(difference as any)?.productData?.enhancedListingData?.pricingData?.from}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              <Editor
                                initialValue={(difference as any)?.productData?.enhancedListingData?.pricingData?.to}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.enhancedListingData?.screenshots && (
                          <tr>
                            <td className="p-4 border-b">Screenshots</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.productData?.enhancedListingData?.screenshots?.from.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.image?.data?.attributes?.url && (
                                      <Image
                                        src={item?.image?.data?.attributes?.url}
                                        alt={item?.title}
                                        width={100}
                                        height={100}
                                        className="rounded"
                                      />
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.productData?.enhancedListingData?.screenshots?.to.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.image?.data?.attributes?.url && (
                                      <Image
                                        src={item?.image?.data?.attributes?.url}
                                        alt={item?.title}
                                        width={100}
                                        height={100}
                                        className="rounded"
                                      />
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                          </tr>
                        )}

                        {(difference as any)?.productData?.enhancedListingData?.videos && (
                          <tr>
                            <td className="p-4 border-b">Videos</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.productData?.enhancedListingData?.videos?.from.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.url && (
                                      <iframe
                                        src={item?.url}
                                        title={item?.title}
                                        width="80%"
                                        height="200"
                                        allowFullScreen
                                      ></iframe>
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.productData?.enhancedListingData?.videos?.to.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.url && (
                                      <iframe
                                        src={item?.url}
                                        title={item?.title}
                                        width="80%"
                                        height="200"
                                        allowFullScreen
                                      ></iframe>
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.productData?.enhancedListingData?.testimonials && (
                          <tr>
                            <td className="p-4 border-b">Testimonials</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.productData?.enhancedListingData?.testimonials?.from.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    <div>
                                      <span>Description :</span>{' '}
                                      <Editor
                                        initialValue={item?.description}
                                        onEditorChange={() => {}}
                                        readOnly={true}
                                      />
                                      <br />
                                      <span>Role :</span> {item?.author?.role}
                                      <br />
                                      <span>Company :</span> {item?.author?.company}
                                      <br />
                                      <span>Name :</span> {item?.author?.name}
                                      <br />
                                      <span>Avatar :</span>
                                      {item?.author?.avatar?.data?.attributes?.url ? (
                                        <Image
                                          src={item?.author?.avatar?.data?.attributes?.url}
                                          alt={`${item?.author?.name}'s avatar`}
                                          width={100}
                                          height={100}
                                          className="rounded-full"
                                        />
                                      ) : (
                                        <span>No Avatar</span>
                                      )}
                                    </div>
                                  </div>
                                ),
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.productData?.enhancedListingData?.testimonials?.to.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    <div>
                                      <span>Description :</span>{' '}
                                      <Editor
                                        initialValue={item?.description}
                                        onEditorChange={() => {}}
                                        readOnly={true}
                                      />
                                      <br />
                                      <span>Role :</span> {item?.author?.role}
                                      <br />
                                      <span>Company :</span> {item?.author?.company}
                                      <br />
                                      <span>Name :</span> {item?.author?.name}
                                      <br />
                                      <span>Avatar :</span>
                                      {item?.author?.avatar?.data?.attributes?.url ? (
                                        <Image
                                          src={item?.author?.avatar?.data?.attributes?.url}
                                          alt={`${item?.author?.name}'s avatar`}
                                          width={100}
                                          height={100}
                                          className="rounded-full"
                                        />
                                      ) : (
                                        <span>No Avatar</span>
                                      )}
                                    </div>
                                  </div>
                                ),
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    )}

                    {solutionType === 'ALSP' && (
                      <>
                        {(difference as any)?.alspData?.serviceName && (
                          <tr>
                            <td className="p-4 border-b">Service Name</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.serviceName?.from}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.serviceName?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.size && (
                          <tr>
                            <td className="p-4 border-b">Size</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.size?.from}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.size?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.partners && (
                          <tr>
                            <td className="p-4 border-b">Partners</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.partners?.from}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.partners?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.fundingAmount && (
                          <tr>
                            <td className="p-4 border-b">Funding Amount</td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingAmountsC.find(
                                  (item: any) => item.value === (difference as any)?.alspData?.fundingAmount?.from,
                                )?.label
                              }
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingAmountsC.find(
                                  (item: any) => item.value === (difference as any)?.alspData?.fundingAmount?.to,
                                )?.label
                              }
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.fundingInfo && (
                          <tr>
                            <td className="p-4 border-b">Funding Info</td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingInfosC.find(
                                  (item: any) => item.value === (difference as any)?.alspData?.fundingInfo?.from,
                                )?.label
                              }
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingInfosC.find(
                                  (item: any) => item.value === (difference as any)?.alspData?.fundingInfo?.to,
                                )?.label
                              }
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.valueProposition && (
                          <tr>
                            <td className="p-4 border-b">Value Proposition</td>
                            <td className="p-4 border-b border-l break-words">
                              <Editor
                                initialValue={(difference as any)?.alspData?.valueProposition?.from}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              <Editor
                                initialValue={(difference as any)?.alspData?.valueProposition?.to}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.audiences && (
                          <tr>
                            <td className="p-4 border-b">Audiences</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.audiences?.data?.from.map((item: any, index: number) => (
                                <div key={index}>{item?.attributes?.name}</div>
                              ))}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.audiences?.data?.to.map((item: any, index: number) => (
                                <div key={index}>{item?.attributes?.name}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.enhancedListingData?.callToAction && (
                          <tr>
                            <td className="p-4 border-b">Call to Action</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.alspData?.enhancedListingData?.callToAction?.label?.from && (
                                <>
                                  Label: {(difference as any)?.alspData?.enhancedListingData?.callToAction?.label?.from}
                                  <br />
                                </>
                              )}
                              {(difference as any)?.alspData?.enhancedListingData?.callToAction?.url?.from && (
                                <>
                                  URL:
                                  <a
                                    href={(difference as any)?.alspData?.enhancedListingData?.callToAction?.url?.from}
                                    className="text-blue-600 underline break-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {(difference as any)?.alspData?.enhancedListingData?.callToAction?.url?.from}
                                  </a>
                                </>
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.alspData?.enhancedListingData?.callToAction?.label?.to && (
                                <>
                                  Label: {(difference as any)?.alspData?.enhancedListingData?.callToAction?.label?.to}
                                  <br />
                                </>
                              )}
                              {(difference as any)?.alspData?.enhancedListingData?.callToAction?.url?.to && (
                                <>
                                  URL:
                                  <a
                                    href={(difference as any)?.alspData?.enhancedListingData?.callToAction?.url?.to}
                                    className="text-blue-600 underline break-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {(difference as any)?.alspData?.enhancedListingData?.callToAction?.url?.to}
                                  </a>
                                </>
                              )}
                            </td>
                          </tr>
                        )}

                        {(difference as any)?.alspData?.enhancedListingData?.serviceDescription && (
                          <tr>
                            <td className="p-4 border-b">Service Description</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.enhancedListingData?.serviceDescription?.from
                                ?.replace(/<\/?[^>]+(>|$)/g, '')
                                .replace(/&nbsp;/g, '')}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.enhancedListingData?.serviceDescription?.to
                                ?.replace(/<\/?[^>]+(>|$)/g, '')
                                .replace(/&nbsp;/g, '')}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.enhancedListingData?.technologyPartners && (
                          <tr>
                            <td className="p-4 border-b">Technology Partners</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.enhancedListingData?.technologyPartners?.from}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.enhancedListingData?.technologyPartners?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.enhancedListingData?.averageLengthOfEngagement && (
                          <tr>
                            <td className="p-4 border-b">Average Length Of Engagement</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.enhancedListingData?.averageLengthOfEngagement?.from}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.alspData?.enhancedListingData?.averageLengthOfEngagement?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.enhancedListingData?.dedicatedResources && (
                          <>
                            <tr>
                              <td className="p-4 border-b">Dedicated Resources</td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.alspData?.enhancedListingData?.dedicatedResources.from.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Count: {item?.count}</span>
                                      <br />
                                      <span>Type: {item?.type}</span>
                                    </div>
                                  ),
                                )}
                              </td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.alspData?.enhancedListingData?.dedicatedResources.to.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Count: {item?.count}</span>
                                      <br />
                                      <span>Type: {item?.type}</span>
                                    </div>
                                  ),
                                )}
                              </td>
                            </tr>
                          </>
                        )}

                        {(difference as any)?.alspData?.enhancedListingData?.pricingData && (
                          <tr>
                            <td className="p-4 border-b">Pricing Data</td>
                            <td className="p-4 border-b border-l break-all">
                              <Editor
                                initialValue={(difference as any)?.alspData?.enhancedListingData?.pricingData?.from}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              <Editor
                                initialValue={(difference as any)?.alspData?.enhancedListingData?.pricingData?.to}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.alspData?.enhancedListingData?.specialists && (
                          <>
                            <tr>
                              <td className="p-4 border-b">Specialists</td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.alspData?.enhancedListingData?.specialists?.from.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Name: {item?.name}</span>
                                      <br />
                                      <span>Role: {item?.role}</span>
                                      <br />
                                      <span>Bio:</span>
                                      <Editor initialValue={item?.bio} onEditorChange={() => {}} readOnly={true} />
                                      <br />
                                      {item?.avatar?.data?.attributes?.url && (
                                        <Image
                                          src={item?.avatar?.data?.attributes?.url}
                                          alt={`${item?.name}'s avatar`}
                                          width={100}
                                          height={100}
                                          className="rounded"
                                        />
                                      )}
                                    </div>
                                  ),
                                )}
                              </td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.alspData?.enhancedListingData?.specialists?.to.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Name: {item?.name}</span>
                                      <br />
                                      <span>Role: {item?.role}</span>
                                      <br />
                                      <span>Bio:</span>
                                      <Editor initialValue={item?.bio} onEditorChange={() => {}} readOnly={true} />
                                      <br />
                                      {item?.avatar?.data?.attributes?.url && (
                                        <Image
                                          src={item?.avatar?.data?.attributes?.url}
                                          alt={`${item?.name}'s avatar`}
                                          width={100}
                                          height={100}
                                          className="rounded"
                                        />
                                      )}
                                    </div>
                                  ),
                                )}
                              </td>
                            </tr>
                          </>
                        )}

                        {(difference as any)?.alspData?.enhancedListingData?.screenshots && (
                          <tr>
                            <td className="p-4 border-b">Screenshots</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.alspData?.enhancedListingData?.screenshots?.from.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.image?.data?.attributes?.url && (
                                      <Image
                                        src={item?.image?.data?.attributes?.url}
                                        alt={item?.title}
                                        width={100}
                                        height={100}
                                        className="rounded"
                                      />
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.alspData?.enhancedListingData?.screenshots?.to.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.image?.data?.attributes?.url && (
                                      <Image
                                        src={item?.image?.data?.attributes?.url}
                                        alt={item?.title}
                                        width={100}
                                        height={100}
                                        className="rounded"
                                      />
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                          </tr>
                        )}

                        {(difference as any)?.alspData?.enhancedListingData?.videos && (
                          <tr>
                            <td className="p-4 border-b">Videos</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.alspData?.enhancedListingData?.videos?.from.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.url && (
                                      <iframe
                                        src={item?.url}
                                        title={item?.title}
                                        width="80%"
                                        height="200"
                                        allowFullScreen
                                      ></iframe>
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.alspData?.enhancedListingData?.videos?.to.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.url && (
                                      <iframe
                                        src={item?.url}
                                        title={item?.title}
                                        width="80%"
                                        height="200"
                                        allowFullScreen
                                      ></iframe>
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                          </tr>
                        )}

                        {(difference as any)?.alspData?.enhancedListingData?.caseStudies && (
                          <>
                            <tr>
                              <td className="p-4 border-b">Case Studies</td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.alspData?.enhancedListingData?.caseStudies?.from.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Name: {item?.name}</span>
                                      <br />
                                      <span>
                                        Link:
                                        <a
                                          href={item?.link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-500 underline"
                                        >
                                          {item?.link}
                                        </a>
                                      </span>
                                      <br />
                                    </div>
                                  ),
                                )}
                              </td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.alspData?.enhancedListingData?.caseStudies?.to.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Name: {item?.name}</span>
                                      <br />
                                      <span>
                                        Link:
                                        <a
                                          href={item?.link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-500 underline"
                                        >
                                          {item?.link}
                                        </a>
                                      </span>
                                      <br />
                                    </div>
                                  ),
                                )}
                              </td>
                            </tr>
                          </>
                        )}

                        {(difference as any)?.alspData?.enhancedListingData?.testimonials && (
                          <tr>
                            <td className="p-4 border-b">Testimonials</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.alspData?.enhancedListingData?.testimonials?.from.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    <div>
                                      <span>Description :</span>{' '}
                                      <Editor
                                        initialValue={item?.description}
                                        onEditorChange={() => {}}
                                        readOnly={true}
                                      />
                                      <br />
                                      <span>Role :</span> {item?.author?.role}
                                      <br />
                                      <span>Company :</span> {item?.author?.company}
                                      <br />
                                      <span>Name :</span> {item?.author?.name}
                                      <br />
                                      <span>Avatar :</span>
                                      {item?.author?.avatar?.data?.attributes?.url ? (
                                        <Image
                                          src={item?.author?.avatar?.data?.attributes?.url}
                                          alt={`${item?.author?.name}'s avatar`}
                                          width={100}
                                          height={100}
                                          className="rounded-full"
                                        />
                                      ) : (
                                        <span>No Avatar</span>
                                      )}
                                    </div>
                                  </div>
                                ),
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.alspData?.enhancedListingData?.testimonials?.to.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    <div>
                                      <span>Description :</span>{' '}
                                      <Editor
                                        initialValue={item?.description}
                                        onEditorChange={() => {}}
                                        readOnly={true}
                                      />
                                      <br />
                                      <span>Role :</span> {item?.author?.role}
                                      <br />
                                      <span>Company :</span> {item?.author?.company}
                                      <br />
                                      <span>Name :</span> {item?.author?.name}
                                      <br />
                                      <span>Avatar :</span>
                                      {item?.author?.avatar?.data?.attributes?.url ? (
                                        <Image
                                          src={item?.author?.avatar?.data?.attributes?.url}
                                          alt={`${item?.author?.name}'s avatar`}
                                          width={100}
                                          height={100}
                                          className="rounded-full"
                                        />
                                      ) : (
                                        <span>No Avatar</span>
                                      )}
                                    </div>
                                  </div>
                                ),
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    )}

                    {solutionType === 'Consultancy' && (
                      <>
                        {(difference as any)?.consultantData?.serviceName && (
                          <tr>
                            <td className="p-4 border-b">Service Name</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.serviceName?.from}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.serviceName?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.size && (
                          <tr>
                            <td className="p-4 border-b">Size</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.size?.from}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.size?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.partners && (
                          <tr>
                            <td className="p-4 border-b">Partners</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.partners?.from}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.partners?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.fundingAmount && (
                          <tr>
                            <td className="p-4 border-b">Funding Amount</td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingAmountsC.find(
                                  (item: any) =>
                                    item.value === (difference as any)?.consultantData?.fundingAmount?.from,
                                )?.label
                              }
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingAmountsC.find(
                                  (item: any) => item.value === (difference as any)?.consultantData?.fundingAmount?.to,
                                )?.label
                              }
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.fundingInfo && (
                          <tr>
                            <td className="p-4 border-b">Funding Info</td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingInfosC.find(
                                  (item: any) => item.value === (difference as any)?.consultantData?.fundingInfo?.from,
                                )?.label
                              }
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                fundingInfosC.find(
                                  (item: any) => item.value === (difference as any)?.consultantData?.fundingInfo?.to,
                                )?.label
                              }
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.valueProposition && (
                          <tr>
                            <td className="p-4 border-b">Value Proposition</td>
                            <td className="p-4 border-b border-l break-words">
                              <Editor
                                initialValue={(difference as any)?.consultantData?.valueProposition?.from}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              <Editor
                                initialValue={(difference as any)?.consultantData?.valueProposition?.to}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.audiences && (
                          <tr>
                            <td className="p-4 border-b">Audiences</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.audiences?.data?.from.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.audiences?.data?.to.map(
                                (item: any, index: number) => <div key={index}>{item?.attributes?.name}</div>,
                              )}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.enhancedListingData?.callToAction && (
                          <tr>
                            <td className="p-4 border-b">Call to Action</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.consultantData?.enhancedListingData?.callToAction?.label?.from && (
                                <>
                                  Label:{' '}
                                  {(difference as any)?.consultantData?.enhancedListingData?.callToAction?.label?.from}
                                  <br />
                                </>
                              )}
                              {(difference as any)?.consultantData?.enhancedListingData?.callToAction?.url?.from && (
                                <>
                                  URL:
                                  <a
                                    href={
                                      (difference as any)?.consultantData?.enhancedListingData?.callToAction?.url?.from
                                    }
                                    className="text-blue-600 underline break-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {(difference as any)?.consultantData?.enhancedListingData?.callToAction?.url?.from}
                                  </a>
                                </>
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.consultantData?.enhancedListingData?.callToAction?.label?.to && (
                                <>
                                  Label:{' '}
                                  {(difference as any)?.consultantData?.enhancedListingData?.callToAction?.label?.to}
                                  <br />
                                </>
                              )}
                              {(difference as any)?.consultantData?.enhancedListingData?.callToAction?.url?.to && (
                                <>
                                  URL:
                                  <a
                                    href={
                                      (difference as any)?.consultantData?.enhancedListingData?.callToAction?.url?.to
                                    }
                                    className="text-blue-600 underline break-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {(difference as any)?.consultantData?.enhancedListingData?.callToAction?.url?.to}
                                  </a>
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.enhancedListingData?.serviceDescription && (
                          <tr>
                            <td className="p-4 border-b">Service Description</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.enhancedListingData?.serviceDescription?.from
                                ?.replace(/<\/?[^>]+(>|$)/g, '')
                                .replace(/&nbsp;/g, '')}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.enhancedListingData?.serviceDescription?.to
                                ?.replace(/<\/?[^>]+(>|$)/g, '')
                                .replace(/&nbsp;/g, '')}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.enhancedListingData?.technologyPartners && (
                          <tr>
                            <td className="p-4 border-b">Technology Partners</td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.enhancedListingData?.technologyPartners?.from}
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.enhancedListingData?.technologyPartners?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.enhancedListingData?.averageLengthOfEngagement && (
                          <tr>
                            <td className="p-4 border-b">Average Length Of Engagement</td>
                            <td className="p-4 border-b border-l break-words">
                              {
                                (difference as any)?.consultantData?.enhancedListingData?.averageLengthOfEngagement
                                  ?.from
                              }
                            </td>
                            <td className="p-4 border-b border-l break-words">
                              {(difference as any)?.consultantData?.enhancedListingData?.averageLengthOfEngagement?.to}
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.enhancedListingData?.dedicatedResources && (
                          <>
                            <tr>
                              <td className="p-4 border-b">Dedicated Resources</td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.consultantData?.enhancedListingData?.dedicatedResources.from.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Count: {item?.count}</span>
                                      <br />
                                      <span>Type: {item?.type}</span>
                                    </div>
                                  ),
                                )}
                              </td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.consultantData?.enhancedListingData?.dedicatedResources.to.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Count: {item?.count}</span>
                                      <br />
                                      <span>Type: {item?.type}</span>
                                    </div>
                                  ),
                                )}
                              </td>
                            </tr>
                          </>
                        )}
                        {(difference as any)?.consultantData?.enhancedListingData?.pricingData && (
                          <tr>
                            <td className="p-4 border-b">Pricing Data</td>
                            <td className="p-4 border-b border-l break-all">
                              <Editor
                                initialValue={
                                  (difference as any)?.consultantData?.enhancedListingData?.pricingData?.from
                                }
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              <Editor
                                initialValue={(difference as any)?.consultantData?.enhancedListingData?.pricingData?.to}
                                onEditorChange={() => {}}
                                readOnly={true}
                              />
                            </td>
                          </tr>
                        )}
                        {(difference as any)?.consultantData?.enhancedListingData?.specialists && (
                          <>
                            <tr>
                              <td className="p-4 border-b">Specialists</td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.consultantData?.enhancedListingData?.specialists?.from.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Name: {item?.name}</span>
                                      <br />
                                      <span>Role: {item?.role}</span>
                                      <br />
                                      <span>Bio:</span>
                                      <Editor initialValue={item?.bio} onEditorChange={() => {}} readOnly={true} />
                                      <br />
                                      {item?.avatar?.data?.attributes?.url && (
                                        <Image
                                          src={item?.avatar?.data?.attributes?.url}
                                          alt={`${item?.name}'s avatar`}
                                          width={100}
                                          height={100}
                                          className="rounded"
                                        />
                                      )}
                                    </div>
                                  ),
                                )}
                              </td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.consultantData?.enhancedListingData?.specialists?.to.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Name: {item?.name}</span>
                                      <br />
                                      <span>Role: {item?.role}</span>
                                      <br />
                                      <span>Bio:</span>
                                      <Editor initialValue={item?.bio} onEditorChange={() => {}} readOnly={true} />
                                      <br />
                                      {item?.avatar?.data?.attributes?.url && (
                                        <Image
                                          src={item?.avatar?.data?.attributes?.url}
                                          alt={`${item?.name}'s avatar`}
                                          width={100}
                                          height={100}
                                          className="rounded"
                                        />
                                      )}
                                    </div>
                                  ),
                                )}
                              </td>
                            </tr>
                          </>
                        )}

                        {(difference as any)?.consultantData?.enhancedListingData?.screenshots && (
                          <tr>
                            <td className="p-4 border-b">Screenshots</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.consultantData?.enhancedListingData?.screenshots?.from.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.image?.data?.attributes?.url && (
                                      <Image
                                        src={item?.image?.data?.attributes?.url}
                                        alt={item?.title}
                                        width={100}
                                        height={100}
                                        className="rounded"
                                      />
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.consultantData?.enhancedListingData?.screenshots?.to.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.image?.data?.attributes?.url && (
                                      <Image
                                        src={item?.image?.data?.attributes?.url}
                                        alt={item?.title}
                                        width={100}
                                        height={100}
                                        className="rounded"
                                      />
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                          </tr>
                        )}

                        {(difference as any)?.consultantData?.enhancedListingData?.videos && (
                          <tr>
                            <td className="p-4 border-b">Videos</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.consultantData?.enhancedListingData?.videos?.from.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.url && (
                                      <iframe
                                        src={item?.url}
                                        title={item?.title}
                                        width="80%"
                                        height="200"
                                        allowFullScreen
                                      ></iframe>
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.consultantData?.enhancedListingData?.videos?.to.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    Title: {item?.title}
                                    <br />
                                    Summary: {item?.summary?.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '')}
                                    <br />
                                    {item?.url && (
                                      <iframe
                                        src={item?.url}
                                        title={item?.title}
                                        width="80%"
                                        height="200"
                                        allowFullScreen
                                      ></iframe>
                                    )}
                                  </div>
                                ),
                              )}
                            </td>
                          </tr>
                        )}

                        {(difference as any)?.consultantData?.enhancedListingData?.caseStudies && (
                          <>
                            <tr>
                              <td className="p-4 border-b">Case Studies</td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.consultantData?.enhancedListingData?.caseStudies?.from.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Name: {item?.name}</span>
                                      <br />
                                      <span>
                                        Link:
                                        <a
                                          href={item?.link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-500 underline"
                                        >
                                          {item?.link}
                                        </a>
                                      </span>
                                      <br />
                                    </div>
                                  ),
                                )}
                              </td>
                              <td className="p-4 border-b border-l break-words">
                                {(difference as any)?.consultantData?.enhancedListingData?.caseStudies?.to.map(
                                  (item: any, index: number) => (
                                    <div className="py-2 border-b" key={index}>
                                      <span>Name: {item?.name}</span>
                                      <br />
                                      <span>
                                        Link:
                                        <a
                                          href={item?.link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-500 underline"
                                        >
                                          {item?.link}
                                        </a>
                                      </span>
                                      <br />
                                    </div>
                                  ),
                                )}
                              </td>
                            </tr>
                          </>
                        )}

                        {(difference as any)?.consultantData?.enhancedListingData?.testimonials && (
                          <tr>
                            <td className="p-4 border-b">Testimonials</td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.consultantData?.enhancedListingData?.testimonials?.from.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    <div>
                                      <span>Description :</span>{' '}
                                      <Editor
                                        initialValue={item?.description}
                                        onEditorChange={() => {}}
                                        readOnly={true}
                                      />
                                      <br />
                                      <span>Role :</span> {item?.author?.role}
                                      <br />
                                      <span>Company :</span> {item?.author?.company}
                                      <br />
                                      <span>Name :</span> {item?.author?.name}
                                      <br />
                                      <span>Avatar :</span>
                                      {item?.author?.avatar?.data?.attributes?.url ? (
                                        <Image
                                          src={item?.author?.avatar?.data?.attributes?.url}
                                          alt={`${item?.author?.name}'s avatar`}
                                          width={100}
                                          height={100}
                                          className="rounded-full"
                                        />
                                      ) : (
                                        <span>No Avatar</span>
                                      )}
                                    </div>
                                  </div>
                                ),
                              )}
                            </td>
                            <td className="p-4 border-b border-l break-all">
                              {(difference as any)?.consultantData?.enhancedListingData?.testimonials?.to.map(
                                (item: any, index: number) => (
                                  <div key={index} className="mb-4">
                                    <div>
                                      <span>Description :</span>{' '}
                                      <Editor
                                        initialValue={item?.description}
                                        onEditorChange={() => {}}
                                        readOnly={true}
                                      />
                                      <br />
                                      <span>Role :</span> {item?.author?.role}
                                      <br />
                                      <span>Company :</span> {item?.author?.company}
                                      <br />
                                      <span>Name :</span> {item?.author?.name}
                                      <br />
                                      <span>Avatar :</span>
                                      {item?.author?.avatar?.data?.attributes?.url ? (
                                        <Image
                                          src={item?.author?.avatar?.data?.attributes?.url}
                                          alt={`${item?.author?.name}'s avatar`}
                                          width={100}
                                          height={100}
                                          className="rounded-full"
                                        />
                                      ) : (
                                        <span>No Avatar</span>
                                      )}
                                    </div>
                                  </div>
                                ),
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export async function getStaticPaths() {
  const start = 0
  const { solutions } = await apolloSdk.allSolutionSlugs({ start })
  const result = solutions?.data || []
  const paths =
    result &&
    result.map((solution) => {
      const slug = (solution && solution.attributes?.slug) || ''
      return {
        params: { slug },
      }
    })
  return {
    paths,
    fallback: 'blocking',
  }
}

// Fetch solution data at build time or on-demand based on the slug
export const getStaticProps: GetStaticProps = async (context) => {
  const { slug } = context.params as { slug: string }

  try {
    let solution = await getFullSolutionsBySlugs([slug], IEnum_Solution_Type.Product)
    if (solution.length <= 0) {
      solution = await getFullSolutionsBySlugs([slug], IEnum_Solution_Type.Alsp)
      if (solution.length <= 0) {
        solution = await getFullSolutionsBySlugs([slug], IEnum_Solution_Type.Consultancy)
      }
    }

    // Fetch solution requests by slug
    const { solutionRequests: solutionReqs } = await apolloSdk.solutionRequestsBySulg({ slug })
    return {
      props: {
        solution: solution.length > 0 ? solution[0] : null,
        solutionReqs: solutionReqs?.data[0] || null,
        slug: slug,
      },
      revalidate: 60,
    }
  } catch (error) {
    return {
      props: {
        solution: null,
        solutionReqs: null,
      },
    }
  }
}

export default Comparison
